import { useRef, useState } from "react";
import { SUREKLILIK_BELGE_ALANLARI, WPQ_ALANLARI, hedefEsit, wpqEtiketi, type GelenHedef, type WpqKarti } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { api } from "@/lib/api";
import {
  OKUMA_UCU,
  WPQ_UCU,
  okumaDugmesi,
  okumaEylemleri,
  wpqFormu,
  wpqKarsilastirmaSatirlari,
  wpqNedenMetni,
  wpqSureklilikMetni,
  wpqSureklilikSonucu,
  wpqYenidenMetni,
  wpqYeterlilikMetni,
  wpqYeterlilikSonucu,
} from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarih, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 3 · PR 15: Documents kartının kaynakçı yeterlilik belgesi (WPQ / WPQR) ve süreklilik kaydı "Document reading" bölümü (prototip belgeler--wpq-okuma,
 * kart 1-4; `NdtSertifikaOkumaBolumu` iskeleti). Okuma yalnız eşlemenin, kuralların ve kişi onaylı kayıt eklemenin kullandığı değerleri gösterir (sözleşme §8):
 * WPQ'da 5 belge değeri, yeterlilik satırında 3, süreklilik satırında 2 değer; süreklilik kaydında 2 belge değeri. Kişisel veri (kimlik no, doğum tarihi ve
 * yeri, fotoğraf, işveren, imza) hiç okunmaz; süreklilik satırından yalnız imzanın var olup olmadığı okunur. OASIS kaynakçıyı kodla, kod yoksa adla önerir;
 * "Use this place" kartın bugünkü tür+yer kaydını çağırır, "Use the reading" yalnız dosyalama formunu kesin değerlerle doldurur (sunucuya yazmaz). Dosyalamadan
 * sonra imzalı süreklilik satırları "Add to Registers" ile tek tek, kişinin onayıyla YENİ kütük satırı olarak eklenir; Registers'taki satırlar ve End of validity
 * değişmez. `salt` kapanmış satır içindir: form ve karar düğmeleri yok; "Add to Registers" sunucunun eylemiyle çizilir. Okuma excel'inin adı kartta yoktur.
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";
const TABLO_BASI = "px-2 py-1.5 text-left text-xs font-medium text-ink-3";
const TABLO_HUCRESI = "px-2 py-1.5 align-top [overflow-wrap:anywhere]";
const SATIR = "grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 px-2.5 py-2 @min-[40rem]:grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1.3fr)]";
const ISO_GUN = /^\d{4}-\d{2}-\d{2}$/;
const TARIH_ALANLARI = ["test_tarihi", "gecerlilik_sonu"];

const aralik = (s: { bas: number; son: number }) => (s.bas === s.son ? String(s.bas) : `${s.bas}-${s.son}`);
const durumTonu = (d: "kisi" | "oneri" | "yok") => (d === "kisi" ? "ok" : d === "oneri" ? "info" : "neutral");

export interface WpqFormYardimi {
  /** "Use the reading": dosyalama formunun alanları (yalnız kesin değerler) */
  kullan: (alanlar: Record<string, string>) => void;
  /** "Use this place": bugünkü tür+yer kaydı */
  yerKaydet: (h: GelenHedef) => Promise<void>;
}

export function WpqOkumaBolumu({ satirId, k, onYenile, salt, hedef, form }: { satirId: string; k: WpqKarti; onYenile: () => void; salt?: boolean; hedef: GelenHedef | null; form?: WpqFormYardimi }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;
  const y = k.yer;
  const wpq = k.cins === "wpq";

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const sayfaLinki = (sayfa: number | null) =>
    o && sayfa !== null ? <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa })} target="_blank" rel="noreferrer">p.{sayfa}</a></> : null;
  const karsilastirmaSatirlari = wpqKarsilastirmaSatirlari(k);
  const yeniden = wpqYenidenMetni(k.yenidenKarsilastirma);
  const toplamUyari = k.karsilastirma?.uyarilar.length ?? 0;
  const eklenebilir = k.sureklilik.some((s) => s.durum === "eklenebilir");
  const kaynakciMetni = y?.kaynakci ? `${y.kaynakci.kod} · ${y.kaynakci.ad}` : null;

  return (
    <div data-bolum="wpq-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && (
        <p className="mt-1 text-sm text-ink-2">
          {wpq
            ? cevir("OASIS reads the welder qualification for you to check. Nothing is filed or changed. Personal data is never read.")
            : cevir("OASIS reads the continuity record for you to check. Nothing is filed or changed. Personal data is never read.")}
        </p>
      )}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <p className="text-xs text-ink-3">
            {[
              o.sayfalar ? (wpq ? cevir("Welder qualification p.{0}", aralik(o.sayfalar)) : cevir("Continuity record p.{0}", aralik(o.sayfalar))) : null,
              k.kod ? cevir("Reading job {0}", k.kod) : null,
              wpq ? cevir("{0} qualification rows", o.yeterlilikSayisi) : null,
              cevir("{0} continuity rows", o.sureklilikSayisi),
            ].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>

          <ul className="mt-2 divide-y divide-line rounded-md border border-line text-sm" data-bolum="bl-parca">
            <li className={SATIR}>
              <span>
                <span className="block text-xs text-ink-3">{cevir("Pages")}</span>
                {o.sayfalar
                  ? <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: o.sayfalar.bas })} target="_blank" rel="noreferrer">p.{aralik(o.sayfalar)}</a>
                  : <span className="text-xs">—</span>}
              </span>
              <span className="min-w-0 [overflow-wrap:anywhere]"><span className="block font-medium">{wpq ? cevir("Qualification") : cevir("Continuity record")}</span></span>
              <span className="col-span-2 min-w-0 [overflow-wrap:anywhere] @min-[40rem]:col-span-1">
                <span className="block text-xs text-ink-3">{cevir("Place")}</span>
                {kaynakciMetni && y && y.durum !== "yok" && <span className="mr-1.5">{kaynakciMetni}</span>}
                {y && y.durum !== "yok"
                  ? <Etiket tone={durumTonu(y.durum)}>{y.durum === "kisi" ? cevir("this place") : cevir("proposed")}</Etiket>
                  : <span className="text-ink-2">{y ? wpqNedenMetni(y.neden) : "—"}</span>}
                {yardim && y?.durum === "oneri" && y.yer && !hedefEsit(y.yer, hedef) && (
                  <Buton kucuk className="mt-1" disabled={mesgul !== null} mesgul={mesgul === "yer"} onClick={() => calistir("yer", () => yardim.yerKaydet(y.yer!))}>{cevir("Use this place")}</Buton>
                )}
              </span>
            </li>
          </ul>

          <AltBaslik>{wpq ? cevir("Qualification") : cevir("Continuity record")}</AltBaslik>
          <ul className="space-y-0.5 text-sm">
            {(wpq ? WPQ_ALANLARI : SUREKLILIK_BELGE_ALANLARI).map((a) => {
              const d = k.degerler.find((x) => x.alan === a.alan);
              const deger = TARIH_ALANLARI.includes(a.alan) && d?.deger && ISO_GUN.test(d.deger) ? tarih(d.deger) : d?.deger;
              return (
                <li key={a.alan} className="flex gap-2">
                  <Nokta k={d?.kesinlik ?? "bos"} />
                  <span className="w-[10rem] shrink-0 text-ink-2">{cevir(wpqEtiketi(a.alan))}</span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {deger ?? <span className="text-ink-3">{cevir("not read")}</span>}
                    {sayfaLinki(d?.sayfa ?? null)}
                    {d?.yazildigi && d.yazildigi !== d.deger && <span className="block text-xs text-ink-3">{cevir("as written: {0}", d.yazildigi)}</span>}
                  </span>
                </li>
              );
            })}
          </ul>

          {wpq && k.yeterlilikler.length > 0 && (
            <>
              <AltBaslik>{cevir("Qualification rows")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Row")}</th>
                    <th className={TABLO_BASI}>{cevir("Process")} · {cevir("WPS")}</th>
                    <th className={TABLO_BASI}>{cevir("Valid until")}</th>
                    <th className={TABLO_BASI}>{cevir("Result")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.yeterlilikler.map((s) => {
                      const suphe = s.degerler.some((d) => d.kesinlik === "suphe");
                      const sayfa = s.degerler.find((d) => d.sayfa !== null)?.sayfa ?? null;
                      return (
                        <tr key={s.sira} data-satir={s.sira}>
                          <td className={cn(TABLO_HUCRESI, "font-mono")}>{s.sira}</td>
                          <td className={TABLO_HUCRESI}>
                            <span className="inline-flex items-center gap-1.5"><Nokta k={suphe ? "suphe" : "kesin"} />{wpqYeterlilikMetni(s)}</span>
                            {sayfaLinki(sayfa)}
                          </td>
                          <td className={TABLO_HUCRESI}>{s.bitis ? tarih(s.bitis) : "—"}</td>
                          <td className={TABLO_HUCRESI}>{wpqYeterlilikSonucu(s)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {k.sureklilik.length > 0 && (
            <>
              <AltBaslik>{cevir("Continuity rows")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Row")}</th>
                    <th className={TABLO_BASI}>{cevir("On the document")}</th>
                    <th className={TABLO_BASI}>{cevir("In Registers")}</th>
                    <th className={TABLO_BASI}>{cevir("Result")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.sureklilik.map((s) => {
                      const suphe = s.degerler.some((d) => d.kesinlik === "suphe");
                      const sayfa = s.degerler.find((d) => d.sayfa !== null)?.sayfa ?? null;
                      const ekleyebilir = s.durum === "eklenebilir" && k.eylemler.sureklilikEkle && !s.engel;
                      return (
                        <tr key={s.sira} data-satir={s.sira}>
                          <td className={cn(TABLO_HUCRESI, "font-mono")}>{s.sira}</td>
                          <td className={TABLO_HUCRESI}>
                            <span className="inline-flex items-center gap-1.5"><Nokta k={suphe ? "suphe" : "kesin"} />{wpqSureklilikMetni(s)}</span>
                            {sayfaLinki(sayfa)}
                          </td>
                          <td className={TABLO_HUCRESI}>{s.kayit ? cevir("Continuity · {0} · p. {1}", s.kayit.tarih ? tarih(s.kayit.tarih) : "—", s.kayit.sayfaBas ?? "—") : "—"}</td>
                          <td className={TABLO_HUCRESI}>
                            {ekleyebilir
                              ? (
                                <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === `ekle-${s.sira}`} onClick={() => calistir(`ekle-${s.sira}`, () => api.post(WPQ_UCU.sureklilik(satirId), { sira: s.sira }))}>
                                  {cevir("Add to Registers")}
                                </Buton>
                              )
                              : wpqSureklilikSonucu(s)}
                            {s.onay && <span className="block text-xs text-ink-3">{cevir("Added by {0} on {1}", s.onay.kim, tarihSaat(s.onay.ts))}</span>}
                            {s.durum === "eklenebilir" && s.engel && <span className="block text-xs text-ink-3">{cevir(s.engel)}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {eklenebilir && k.eylemler.sureklilikEkle && (
                <p className="mt-1 text-xs text-ink-3">
                  {cevir("“Add to Registers” adds one continuity record to {0} with the date of that row and this PDF (page of the row). Rows already in Registers do not change; End of validity does not change.", y?.kaynakci?.kod ?? cevir("this welder"))}
                </p>
              )}
            </>
          )}

          {yardim && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-bolum="okumayi-kullan">
              <Buton kucuk disabled={mesgul !== null} onClick={() => yardim.kullan(wpqFormu(k))}>{cevir("Use the reading")}</Buton>
              <span className="w-full text-xs text-ink-3">
                {wpq
                  ? cevir("“Use the reading” fills Standard, Date and End of validity with the certain values of the qualification; uncertain and empty values stay empty for you to type. Nothing is filed until you confirm.")
                  : cevir("“Use the reading” fills Date with the newest signed continuity row; an uncertain date stays empty for you to type. Nothing is filed until you confirm.")}
              </span>
            </div>
          )}
          <p className="mt-1 text-xs text-ink-3">
            {wpq
              ? cevir("Not read: the document number, the examining body, P-No / F-No, test results, and the diameter, thickness, position and backing ranges (their rule waits for approval). Personal data (ID number, date and place of birth, photo, employer, signatures and signer names) is never read; for a continuity row only whether it is signed is read.")
              : cevir("Not read: the document number, the signer’s name and title. Personal data (ID number, date and place of birth, photo, employer, signatures) is never read; for a continuity row only whether it is signed is read.")}
          </p>
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {(karsilastirmaSatirlari.length > 0 || yeniden) && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {karsilastirmaSatirlari.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.karsilastirildi ? "kesin" : n.suphe ? "suphe" : "bos"} />
                  <span className="min-w-0">{n.metin}</span>
                </li>
              ))}
              {yeniden && (
                <li className="flex gap-2">
                  <Nokta k="kesin" />
                  <span className="min-w-0">{yeniden}</span>
                </li>
              )}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && toplamUyari === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : toplamUyari === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <ul className="space-y-1.5">
                  {k.karsilastirma.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                </ul>
              )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(WPQ_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(WPQ_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(WPQ_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <Band tip="duvar" className="mt-2">{hata}</Band>}
    </div>
  );
}
